import {
    Directive,
    ElementRef,
    HostBinding,
    HostListener,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SecurityContext,
    inject
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { App } from '@df/core/app';
import { Acl } from '@df/core/service/acl';
import { Client } from '@df/core/service/client';
import { Url } from '@df/core/service/url';
import { getElementHeight } from '@df/dom/fn/get-element-height';
import { getElementWidth } from '@df/dom/fn/get-element-width';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { Renderer } from '@df/ng/renderer';
import { TimeoutService } from '@df/ng/timeout.service';
import { IResizeOptions, ResizeService } from '@df/ui/common/resize.service';
import { Subscription } from 'rxjs';
import { NDfModuleImage } from './df-module-image.interface';

/**
 * Image defintion
 */
export interface IImage {
    url: string;
    alt?: string;
    title?: string;
}
export interface ToImage {
    toImage: () => IImage;
}

interface ISize {
    width: number;
    height: number;
}

type TWidthProvider = (winSize: number) => number;

/**
 * Meta data for image preloader
 */
interface IImageMeta {
    width: number | TWidthProvider;
    /**
     * If undefined then reference is window
     */
    // widthRef ?: ;
    /**
     * Ratio class
     */
    ratio: string;
}

export const IMAGE_INIT_EVENT = 'IMAGE_INIT_EVENT';
export interface IImageEvent {
    directive: ImageDirective;
}

const UNIVERSAL_SIZE_PLACEHOLDER = 919;
@Directive({
    selector: 'img[image], div[image]',
    standalone: true
})
export class ImageDirective implements OnInit, OnDestroy, OnChanges {
    /**
     * Max number of tries to retrieve dimensions
     * @type {Number}
     */
    static MAXTRIES = 20;

    readonly subscriptions: Subscription = new Subscription();

    @Input()
    imageMeta?: IImageMeta;

    // private canReveal = true;

    /**
     * Image to use
     */
    @Input({
        required: true
    })
    set image(value: string | IImage | ToImage | undefined | null | false) {
        const image = value || this.moduleConfig.placeholder;

        if (typeof image === 'string') {
            if (image.match(/^https?:\/\//)) {
                this.imageObject = { url: image };
            } else {
                const parts = image.split(/:|&&/);
                this.imageObject = { url: parts[0], alt: parts[1], title: parts[2] };
            }
        } else if (image && image['toImage']) {
            this.imageObject = (<ToImage>image).toImage();
        } else {
            this.imageObject = <IImage>image;
        }

        this.src = undefined;
    }

    /**
     * Forced height setter
     */
    @Input()
    set imgHeight(height: number) {
        this.forcedHeight = height;
    }

    /**
     * Forced width setter
     */
    @Input()
    set imgWidth(width: number) {
        this.forcedWidth = width;
    }

    /**
     * Forced width setter
     */
    @Input()
    set scale(scale: number) {
        this.forcedScale = scale;
    }
    /**
     * When true image is always visible
     */
    @Input() always = false;

    /**
     * Optional exclusions pattern
     */
    @Input() exclude?: string | RegExp;

    /**
     * Optional exclusions pattern
     */
    @Input() options?: Partial<IResizeOptions>;

    /**
     * Optiosn buffer for change detection in CMS
     */
    lastOptions?: Partial<IResizeOptions>;

    /**
     * When true image will be set as container background
     */
    @Input() background?: boolean;

    /**
     * Allow image size for retina displays
     */
    @Input() retina = true;

    /**
     * False if you want to skip resize service
     */
    @Input() noResize = false;

    @Input() viewportTollerance?: number;

    /**
     * Image source
     */
    private _src?: string;
    private _properSrc?: string;
    set src(src: string | undefined) {
        if (src !== this._src) {
            this.renderer.removeClass(this.elementRef.nativeElement, 'loaded');
            this._src = src;
            if (src) {
                if (this.background) {
                    const bg = this.domSanitizer.bypassSecurityTrustStyle(`url('${src}')`);
                    this.renderer.setStyle(
                        this.elementRef.nativeElement,
                        'background-image',
                        this.domSanitizer.sanitize(SecurityContext.STYLE, bg)
                    );
                } else {
                    this.renderer.setAttribute(this.elementRef.nativeElement, 'src', src);
                }
            } else {
                this.renderer.removeAttribute(this.elementRef.nativeElement, 'src');
            }
        }
    }

    get src(): string | undefined {
        return this._src;
    }

    /**
     * Image alt
     */
    private _alt?: string;
    set alt(alt: string | undefined) {
        if (alt !== this._alt) {
            const element = this.elementRef.nativeElement as HTMLImageElement;
            if (alt) {
                this.renderer.setAttribute(element, 'alt', alt);
            } else {
                const elAlt = element.alt;
                if (!elAlt || elAlt === this._alt) {
                    this.renderer.setAttribute(element, 'alt', '');
                }
            }
            this._alt = alt;
        }
    }

    get alt(): string | undefined {
        return this._alt;
    }

    /**
     * Image title
     */
    private _title?: string;
    set title(title: string | undefined) {
        if (title !== this._title) {
            this._title = title;
            if (title) {
                this.renderer.setAttribute(this.elementRef.nativeElement, 'title', title);
            } else {
                this.renderer.removeAttribute(this.elementRef.nativeElement, 'title');
            }
        }
    }

    get title(): string | undefined {
        return this._title;
    }

    private resizeService = inject(ResizeService);

    /**
     * Image object set using image setter
     */
    private imageObject?: IImage;

    /**
     * Current container height
     * @type {number}
     */
    private currentHeight?: number;

    /**
     * Current container height
     * @type {number}
     */
    private currentWidth?: number;

    /**
     * Viewport changes subscription
     */
    private subscriptionViewport?: Subscription;

    /**
     * Forced image height
     */
    private forcedHeight?: number;

    /**
     * Forced image width
     */
    private forcedWidth?: number;

    /**
     * Forced image scale
     */
    private forcedScale = 1;

    /**
     * If compoentn has been initiated
     */
    private initiated = false;

    constructor(
        private elementRef: ElementRef,
        private ngZone: NgZone,
        private domSanitizer: DomSanitizer,
        private url: Url,
        private client: Client,
        private renderer: Renderer,
        private renderer2: Renderer2,
        private acl: Acl,
        private app: App,
        private timeoutService: TimeoutService,
        @Inject(NDfModuleImage.OPTIONS) private moduleConfig: NDfModuleImage.IModuleOptions
    ) {
        this.renderer.addClass(this.elementRef.nativeElement, 'df-image');
    }

    /////////////////////////////////////////////
    //
    // IMAGE SETTERS
    //
    /////////////////////////////////////////////

    /**
     * Sets image source
     *
     * @param src Image path
     * @param alt Alt attribute
     * @param title Title attribute
     */
    private setSrc(src: string, proper = true, alt?: string, fake = false, title?: string) {
        if (proper) {
            if (this._properSrc === src) {
                return;
            }
            this._properSrc = src;
        }
        if (this.src !== src) {
            if (fake) {
                // DROPPED WITH SSR2 upgrade, but not really needed with shell
                // this.renderer2.setAttribute(this.elementRef.nativeElement, 'imageFakeSrc', src);
                // this.renderer2.setAttribute(
                //     this.elementRef.nativeElement,
                //     'onerror',
                //     `typeof quickImg !=='undefined' && quickImg(this) ;this.removeAttribute('onerror')`
                // );
                // this.renderer2.setAttribute(this.elementRef.nativeElement, 'onload', `this.classList.add('loaded')`);
                // this.canReveal = false;
                // this.src = '.';
            } else {
                this.src = src;
            }

            this.alt = alt || '';

            if (title) {
                this.title = title;
            }

            // whents its prerender we have no time to wait for image to load
            // we need to put loaded class to take opacity:0 off the image
            // if (this.client.isPrerender || !this.app.isPlatformBrowser) {
            //     this.onLoad();
            // }
        }
    }

    /**
     * Sets placeholder image with any text passed
     * Does not apply on production when non-dev mode
     *
     * @param text Set
     */
    private placeholder(text: string) {
        this._properSrc = undefined;
        // this.canReveal = true;
        if (this.acl.isDeveloper || !this.acl.isProduction) {
            this.setSrc(
                `https://place-hold.it/${this.currentWidth || 500}x${this.currentHeight || 500}/f7f4f1/e12954?text=${text}&fontsize=36px`,
                false
            );
        } else if (this.moduleConfig.placeholder) {
            this.setSrc(this.url.toMediaUrl(this.moduleConfig.placeholder), false);
        }
    }

    /////////////////////////////////////////////
    //
    // EVENT HANDLING
    //
    /////////////////////////////////////////////

    /**
     * Subscribes to image size updates
     */
    ngOnInit(): void {
        // initialize from existing source in case we have unviersal rendered content
        this._src = (this.elementRef.nativeElement as HTMLImageElement)?.src;
        // if (this._src) {
        //     this.onLoad();
        // }
        const event: IImageEvent = {
            directive: this
        };

        this.app.events.broadcast(IMAGE_INIT_EVENT, event);

        this.initiated = true;
        this.subscriptions.add(
            resizeObservable(
                this.elementRef.nativeElement,
                (event: ResizeObserverEntry) => {
                    const entry = event.borderBoxSize[0];
                    this.update(this.toImageSize({ height: entry.blockSize, width: entry.inlineSize }));
                },
                0
            ).subscribe()
        );
        this.update(true);
    }

    ngOnChanges(): void {
        this.update();
    }

    /**
     * Cleasn up on destroy
     */
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Sets placeholder on load error
     */
    @HostListener('error')
    onError(): void {
        if (this.app.isPlatformBrowser && !this.client.isPrerender) {
            if (this.src) {
                this.placeholder('Error loading');
            }
        }
    }

    /**
     * Adds 'loaded' class on load success
     */
    // @HostListener('load')
    // onLoad(): void {
    // if (this.src && this.canReveal) {
    //     this.renderer2.addClass(this.elementRef.nativeElement, 'loaded');
    // }
    // }

    @HostBinding('loading')
    get loading(): 'lazy' | 'eager' {
        return this.always ? 'eager' : 'lazy';
    }

    /////////////////////////////////////////////
    //
    // HELPERS
    //
    /////////////////////////////////////////////
    private modularRound(dim: number, max?: number): number {
        if (dim < 1) {
            return 0;
        }

        let rounded = 30;

        while (dim > rounded) {
            rounded *= 1.5;
        }

        const res = Math.ceil(rounded);

        return max ? Math.min(max, res) : res;
    }

    private update(dims?: true | { width: number; height: number }) {
        if (!this.initiated) {
            return;
        }
        // set timeout not to render immediately but in the next cycle (or ng may not render elements yet and flex will stretch things)
        this.timeoutService.setTimeout(() => {
            this._update(dims);
        });
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    readonly updateThrottled = this.timeoutService.throttle(() => this.update(), 100);

    /**
     * Update method
     */
    private _update(dims?: true | { width: number; height: number }) {
        // do nothing if no change
        if (dims && typeof dims == 'object' && dims.width === this.currentWidth && dims.height === this.currentHeight) {
            return;
        }

        this.lastOptions = { ...this.options };
        this.ngZone.runOutsideAngular(() => {
            const image = this.imageObject;
            const element = this.elementRef.nativeElement;
            try {
                // if its svg or matches exclusion then process it without rescalling
                if (image && (this.noResize || image.url.match(/\.(svg|gif)$/) || (this.exclude && image.url.match(this.exclude)))) {
                    // this.canReveal = true;
                    return this.setSrc(this.url.toMediaUrl(image.url), true);
                }

                // Quit if image src starts with 'https' (remote one).
                if (image && image.url.match(/^https?:\/\//)) {
                    // this.canReveal = true;
                    return this.setSrc(image.url, true);
                }

                if (!this.app.isPlatformBrowser) {
                    // fake dimensions to be dealt with later
                    this.currentWidth = UNIVERSAL_SIZE_PLACEHOLDER;
                    this.currentHeight = UNIVERSAL_SIZE_PLACEHOLDER;
                } else {
                    if (dims === true) {
                        dims = this.toImageSize({ width: getElementWidth(element)!, height: getElementHeight(element)! });
                    }
                    if (!dims) {
                        dims = { width: this.currentWidth || 0, height: this.currentHeight || 0 };
                    }

                    // update current values
                    this.currentWidth = dims.width;
                    this.currentHeight = dims.height;

                    const hasCustomWidth = this.forcedWidth !== undefined;
                    const hasCustomHeight = this.forcedHeight !== undefined;
                    // check if dimensions
                    if (
                        !(
                            (hasCustomWidth && hasCustomHeight) ||
                            (this.currentWidth && this.currentHeight) ||
                            (this.currentHeight && hasCustomWidth) ||
                            (this.currentWidth && hasCustomHeight)
                        )
                    ) {
                        throw 'No dimensions';
                    }
                }

                // check if image is set (after dims, otherwise we will not prepare dims on empty element)
                if (!image) {
                    throw new Error('No image');
                }

                // if (this.isViewPriority && !this.app.isPlatformBrowser) {
                //     this.updatePreloadLinks();
                // }
                // this.canReveal = true;
                return this.setSrc(
                    this.toImageSrc(this.currentWidth, this.currentHeight, !this.app.isPlatformBrowser),
                    true,
                    image.alt,
                    !this.app.isPlatformBrowser,
                    image.title
                );
            } catch (err) {
                return this.placeholder(err instanceof Error ? err.message : typeof err === 'string' ? err : 'unexpected error');
            }
        });
    }

    /**
     * Converts container sizes to image file sizes
     */
    private toImageSize(size: ISize, isRetina = this.client.isRetina): ISize {
        const hasCustomWidth = this.forcedWidth !== undefined;
        const hasCustomHeight = this.forcedHeight !== undefined;

        let width = !hasCustomWidth ? size.width : this.forcedWidth || 0;
        let height = !hasCustomHeight ? size.height : this.forcedHeight || 0;
        let maxWidth = this.forcedWidth;
        let maxHeight = this.forcedHeight;
        const round = Math.round;

        width *= this.forcedScale;
        height *= this.forcedScale;

        // double sizes for retina
        if (this.shouldDoubleSize(isRetina)) {
            width *= 2;
            size.width *= 2;
            height *= 2;
            size.height *= 2;
            if (maxWidth) {
                maxWidth *= 2;
            }
            if (maxHeight) {
                maxHeight *= 2;
            }
        }

        // rounding start
        let heightByWidth = true;
        let widthByHeight = true;

        // if width is 0 we cant set by it
        if (!width) {
            heightByWidth = false;
        }

        // if attr width is force then we cant set width by height
        if (hasCustomWidth) {
            widthByHeight = false;
        }

        // if height is 0 we cant set by it
        if (!height) {
            widthByHeight = false;
        }

        // if attr height is forced then we cant set height by width
        if (hasCustomHeight) {
            heightByWidth = false;
        }

        // if we can create dims by width, lets do it
        if (heightByWidth) {
            width = this.modularRound(width, maxWidth);
            height = round((width * size.height) / size.width);
        } else if (widthByHeight) {
            // if cant by width then lets do it by height
            height = this.modularRound(height, maxHeight);
            width = round((height * size.width) / size.height);
        }

        return { width, height };
    }

    /**
     * Converts dimensions to image source
     */
    private toImageSrc(width: number, height: number, strictDims: boolean = false): string {
        if (!this.imageObject) {
            return '';
        }
        return this.resizeService.resize(
            this.url.toMediaUrl(this.imageObject.url),
            Object.assign(
                {
                    width,
                    height,
                    strictDims
                },
                this.options
            )
        );
    }

    private shouldDoubleSize(isRetina = this.client.isRetina): boolean {
        return isRetina && !this.moduleConfig.ignorRetina && this.retina;
    }
}
